import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment-timezone";
import Flatpickr from "flatpickr";
import { Spanish } from "flatpickr/dist/l10n/es.js";

import Header from "../components/Header";
import Footer from "../components/Footer";
import ScrollToTop from "../components/ScrollTop";
import PhoneLoader from "../components/loader/PhoneLoader";

import { ImCalendar } from "react-icons/im";
import { useAuth } from "../provider/authProvider";

import "../Installment.css";

const TZ = "America/Lima";

// Cantidad de estilos de grupo (0..DUP_GROUPS-1). Si hay más grupos, se reciclan.
const DUP_GROUPS = 5;

function Installment(props) {
    const { user, rol } = useAuth();
    const navigate = useNavigate();

    const calendarInputRef = useRef(null);
    const calendarInstanceRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [showText, setShowText] = useState("");

    const [selectedDate, setSelectedDate] = useState(
        moment().tz(TZ).format("YYYY-MM-DD")
    );
    const [selectedRange, setSelectedRange] = useState(() => {
        const today = moment().tz(TZ).format("YYYY-MM-DD");
        return [today, today];
    });

    const [listMovilesPay, setListMovilesPay] = useState([]);
    const [listFmMovilesPay, setListFmMovilesPay] = useState([]);
    const [listDueCredit, setListDueCredit] = useState([]);
    const [listDueFm, setListDueFm] = useState([]);

    const fetchInstallments = async (dateOrRange) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");

            const range = Array.isArray(dateOrRange) ? dateOrRange : [dateOrRange, dateOrRange];
            const startDate = String(range?.[0] || "").trim();
            const endDate = String(range?.[1] || startDate || "").trim();

            const params = new URLSearchParams();
            if (startDate) params.append("startDate", startDate);
            if (endDate) params.append("endDate", endDate);
            if (!startDate && !endDate && dateOrRange) params.append("date", dateOrRange);

            const url = `${process.env.REACT_APP_API}/installments?${params.toString()}`;

            const resp = await axios.get(url, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            const resData = resp.data;
            // console.log(resp.data);
            if (resData?.msg === "success") {
                setListMovilesPay(resData?.data?.movilespay || []);
                setListFmMovilesPay(resData?.data?.fm_movilespay || []);
                setListDueCredit(resData?.data?.due_today_credit || []);
                setListDueFm(resData?.data?.due_today_fm || []);
            } else {
                setListMovilesPay([]);
                setListFmMovilesPay([]);
                setListDueCredit([]);
                setListDueFm([]);
            }
        } catch (e) {
            console.error("[Installment] fetch error:", e);
            setListMovilesPay([]);
            setListFmMovilesPay([]);
            setListDueCredit([]);
            setListDueFm([]);
        } finally {
            setLoading(false);
        }
    };

    /* =========================== FLATPICKR =========================== */
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (!calendarInputRef.current) return;

            if (calendarInstanceRef.current) {
                calendarInstanceRef.current.destroy();
                calendarInstanceRef.current = null;
            }

            calendarInstanceRef.current = Flatpickr(calendarInputRef.current, {
                locale: Spanish,
                mode: "range",
                dateFormat: "Y-m-d",
                defaultDate: selectedRange,
                disable: [
                    function(date) {
                        const maxDate = new Date();
                        const isRole2 = String(rol) === "2";
                        const monthStart = moment().tz(TZ).startOf("month").toDate();

                        const minDateDefault = new Date("2025-07-11");
                        const minDate = isRole2 ? monthStart : minDateDefault;

                        return date < minDate || date > maxDate;
                    },
                ],
                onChange: function(selectedDates) {
                    const start = selectedDates?.[0];
                    const end = selectedDates?.[1];
                    if (!start) return;

                    const startValue = moment(start).tz(TZ).format("YYYY-MM-DD");
                    const endValue = moment(end || start).tz(TZ).format("YYYY-MM-DD");

                    const startText = moment(start)
                        .tz(TZ)
                        .locale("es")
                        .format("dddd, DD [de] MMMM [del] YYYY");

                    const endText = moment(end || start)
                        .tz(TZ)
                        .locale("es")
                        .format("dddd, DD [de] MMMM [del] YYYY");

                    const text = end
                        ? `Mostrando desde "${startText}" hasta "${endText}"`
                        : `Seleccionando desde "${startText}"`;

                    setShowText(text);
                },
                onClose: function(selectedDates) {
                    const start = selectedDates?.[0];
                    const end = selectedDates?.[1];
                    if (!start) return;

                    const startValue = moment(start).tz(TZ).format("YYYY-MM-DD");
                    const endValue = moment(end || start).tz(TZ).format("YYYY-MM-DD");

                    setSelectedDate(endValue);
                    setSelectedRange([startValue, endValue]);

                    const startText = moment(start)
                        .tz(TZ)
                        .locale("es")
                        .format("dddd, DD [de] MMMM [del] YYYY");

                    const endText = moment(end || start)
                        .tz(TZ)
                        .locale("es")
                        .format("dddd, DD [de] MMMM [del] YYYY");

                    const text = startValue === endValue
                        ? `Mostrando "${startText}"`
                        : `Mostrando desde "${startText}" hasta "${endText}"`;

                    setShowText(text);
                    fetchInstallments([startValue, endValue]);
                },
            });
        }, 150);

        return () => {
            clearTimeout(timeout);
            if (calendarInstanceRef.current) {
                calendarInstanceRef.current.destroy();
                calendarInstanceRef.current = null;
            }
        };
        // eslint-disable-next-line
    }, [rol]);

    /* =========================== CARGA INICIAL =========================== */
    useEffect(() => {
        const today = moment().tz(TZ).format("YYYY-MM-DD");
        setSelectedDate(today);
        setSelectedRange([today, today]);
        setShowText(
            `Mostrando "${moment()
                .tz(TZ)
                .locale("es")
                .format("dddd, DD [de] MMMM [del] YYYY")}"`
        );
        fetchInstallments([today, today]);
        // eslint-disable-next-line
    }, []);

    const getFmSourceLabel = (source) => {
        const v = String(source ?? "").trim();
        if (v === "1") return "Payjoy";
        if (v === "2") return "MovilesPay";
        return v || "-";
    };

    const renderFmSourceBadge = (source) => {
        const label = getFmSourceLabel(source);
        const isPayjoy = String(source) === "1";

        return (
            <span className={`fmSourceBadge ${isPayjoy ? "payjoy" : "movilespay"}`}>
                {label}
            </span>
        );
    };

    /* =========================== TOTALES (EFECTIVO / ONLINE / TOTAL) =========================== */
    const parseMoney = (v) => {
        if (v === null || v === undefined) return 0;
        // Acepta "100", "100.00", "S/ 100.00", "100,00", etc.
        const s = String(v)
            .replace(/\s/g, "")
            .replace(/S\//gi, "")
            .replace(/,/g, "");
        const n = Number(s);
        return Number.isFinite(n) ? n : 0;
    };

    const moneyFmt = (n) => {
        try {
            return new Intl.NumberFormat("es-PE", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(Number(n || 0));
        } catch (e) {
            return (Number(n || 0)).toFixed(2);
        }
    };

    // Gestor de cobranza: primer nombre + 3 letras del apellido paterno + "..."
    const formatCobranzaShort = (name, paternalSur) => {
        const first = String(name || "").trim().split(/\s+/)[0] || "";
        const pat = String(paternalSur || "").trim();
        const last3 = pat ? pat.substring(0, 3) : "";
        const last = last3 ? `${last3}...` : "";
        const out = `${first} ${last}`.trim();
        return out || "-";
    };


    const isOnlinePayment = (row) => {
        if (!row) return false;

        // 1) Flags directos (por si tu backend ya lo envía)
        if (row?.is_online === true) return true;
        if (row?.is_online === false) return false;

        // 2) Buscar en campos comunes (MovilesPay y FM pueden variar)
        const rawCandidates = [
            row?.fr_payment_type,
            row?.fr_payment_method,
            row?.fr_payment_way,
            row?.fr_pay_type,
            row?.fr_method,
            row?.payment_type,
            row?.payment_method,
            row?.method,
            row?.pay_method,
            row?.pay_type,

            row?.fmp_payment_type,
            row?.fmp_payment_method,
            row?.fmp_payment_way,
            row?.fmp_pay_type,
            row?.fmp_method,
        ];

        const joined = rawCandidates
            .filter((x) => x !== null && x !== undefined && String(x).trim() !== "")
            .map((x) => String(x).toLowerCase().trim())
            .join(" ");

        // 3) Heurística: keywords típicas de pago online
        const onlineKeywords = [
            "yape",
            "plin",
            "transfer",
            "transferencia",
            "online",
            "bcp",
            "interbank",
            "scotiabank",
            "bbva",
            "deposito",
            "depósito",
            "pos",
            "tarjeta",
            "visa",
            "mastercard",
            "izipay",
            "culqi",
        ];

        if (onlineKeywords.some((k) => joined.includes(k))) return true;

        // 4) Heurística numérica si usas códigos:
        //    - "2" suele ser online en muchos sistemas (si tu caso es distinto, lo ajustas aquí).
        const numericCandidates = rawCandidates
            .map((x) => (x === null || x === undefined ? "" : String(x).trim()))
            .filter(Boolean);

        if (numericCandidates.includes("2")) return true;

        return false;
    };

    const calcDailyTotals = (rows = [], amountKey) => {
        let efectivo = 0;
        let online = 0;

        (rows || []).forEach((r) => {
            const amount = parseMoney(r?.[amountKey]);
            if (isOnlinePayment(r)) online += amount;
            else efectivo += amount;
        });

        const total = efectivo + online;
        return { efectivo, online, total };
    };

    const totalsMovilesPay = useMemo(() => {
        // En tu tabla usas p?.fr_fee como "Monto Pag."
        return calcDailyTotals(listMovilesPay || [], "fr_fee");
    }, [listMovilesPay]);

    const totalsFm = useMemo(() => {
        // En tu tabla usas p?.fmp_fee como "Monto Pag."
        return calcDailyTotals(listFmMovilesPay || [], "fmp_fee");
    }, [listFmMovilesPay]);

    /* =========================== VENCE HOY (Créditos + FM) =========================== */
    const selectedMoment = useMemo(() => {
        const m = moment.tz(selectedDate, "YYYY-MM-DD", true, TZ);
        return m.isValid() ? m : moment().tz(TZ);
    }, [selectedDate]);

    const selectedDDMMYYYY = useMemo(() => selectedMoment.format("DD/MM/YYYY"), [selectedMoment]);

    const safeJsonParse = (raw) => {
        if (raw === null || raw === undefined) return null;
        const s = String(raw).trim();
        if (!s) return null;
        try {
            return JSON.parse(s);
        } catch (e) {
            return null;
        }
    };

    const getScheduleMeta = (scheduleRaw) => {
        const arr = safeJsonParse(scheduleRaw);
        if (!Array.isArray(arr)) {
            return { total: 0, paid: 0, pending: 0, todayItem: null };
        }
        const total = arr.length;
        const paid = arr.filter((x) => String(x?.state) === "1" || Number(x?.state) === 1).length;
        const pending = Math.max(0, total - paid);
        const todayItem = arr.find((x) => String(x?.date || "").trim() === selectedDDMMYYYY) || null;
        return { total, paid, pending, todayItem };
    };

    // ===========================
    // Cuotas vencidas: state=0 y date <= fecha seleccionada (inclusive)
    // ===========================
    const countOverdueFromSchedule = (scheduleRaw) => {
        const arr = safeJsonParse(scheduleRaw);
        if (!Array.isArray(arr) || arr.length === 0) return 0;

        const cutoff = selectedMoment.clone().startOf("day");

        let cnt = 0;
        for (const it of arr) {
            const st = Number(it?.state ?? 0);
            if (st !== 0) continue;

            const dStr = String(it?.date || "").trim();
            if (!dStr) continue;

            const d = moment.tz(dStr, "DD/MM/YYYY", true, TZ);
            if (!d.isValid()) continue;

            if (d.isSameOrBefore(cutoff, "day")) cnt += 1;
        }
        return cnt;
    };


    // =====================================================
    // N° Cuota por REGISTRO en CRÉDITOS (MovilesPay y Due Credit)
    // - 2 cuotas en 1 pago: "19-20/20"
    // - 2 pagos separados: "19/20" y "20/20"
    // =====================================================
    const creditFeeLabelByPaymentId = useMemo(() => {
        const map = new Map(); // key(payment_id) -> label

        const toNum = (v) => {
            const n = Number(String(v ?? "").replace(/S\//gi, "").replace(/,/g, "").trim());
            return Number.isFinite(n) ? n : 0;
        };

        const parseSchedule = (raw) => {
            const arr = safeJsonParse(raw);
            return Array.isArray(arr) ? arr : [];
        };

        const isPaid = (it) => String(it?.state) === "1" || Number(it?.state) === 1;

        const getPaidTotalFromSchedule = (schedule) => {
            let lastPaidIdx = -1;
            for (let i = 0; i < schedule.length; i++) {
                if (isPaid(schedule[i])) lastPaidIdx = i;
            }
            return lastPaidIdx >= 0 ? (lastPaidIdx + 1) : 0;
        };

        const estimatePaidCountByAmount = (amount, feePerQuota) => {
            const amt = toNum(amount);
            const fee = toNum(feePerQuota);
            if (!amt || !fee) return 1;

            const k = Math.max(1, Math.round(amt / fee));
            const diff = Math.abs(amt - (k * fee));
            const tol = 0.25 * k; // tolerancia por redondeos
            return diff <= tol ? k : 1;
        };

        // Unimos ambas listas (movilespay + due_credit) porque ambas tienen pagos del día
        const allRows = [
            ...(listMovilesPay || []).map(r => ({ ...r, __kind: "mp" })),
            ...(listDueCredit || []).map(r => ({ ...r, __kind: "due" })),
        ];

        // Agrupar pagos por credit_id
        const groups = new Map(); // credit_id -> rows con pago
        allRows.forEach((r) => {
            const creditId = String(r?.credit_id ?? "");
            if (!creditId) return;

            // payment id: en MovilesPay es fr_id, en DueCredit es payment_id
            const payId = String(r?.fr_id ?? r?.payment_id ?? "");
            if (!payId) return;

            const arr = groups.get(creditId) || [];
            arr.push(r);
            groups.set(creditId, arr);
        });

        groups.forEach((rows) => {
            // Orden asc por fecha/hora de pago
            rows.sort((a, b) => {
                const da = String(a?.fr_current_date || "");
                const db = String(b?.fr_current_date || "");
                const ma = moment.tz(da, "DD-MM-YYYY HH:mm:ss", true, TZ);
                const mb = moment.tz(db, "DD-MM-YYYY HH:mm:ss", true, TZ);
                return ma.valueOf() - mb.valueOf();
            });

            // Schedule: MovilesPay usa credit_schedule, DueCredit usa fr_days_payment
            const scheduleRaw = rows?.[0]?.credit_schedule ?? rows?.[0]?.fr_days_payment ?? null;
            const schedule = parseSchedule(scheduleRaw);
            const totalFees = schedule.length;
            const paidTotal = getPaidTotalFromSchedule(schedule);

            const feePerQuota = rows?.[0]?.fee;

            // Cuántas cuotas pagó cada registro
            const counts = rows.map((r) => estimatePaidCountByAmount(r?.fr_fee, feePerQuota));
            const paidTodayTotal = counts.reduce((s, x) => s + (Number(x) || 0), 0);

            // Primera cuota pagada HOY
            let cursor = Math.max(1, (paidTotal - paidTodayTotal + 1));

            rows.forEach((r, i) => {
                const payId = String(r?.fr_id ?? r?.payment_id ?? "");
                const k = Math.max(1, Number(counts[i] || 1));

                const startN = cursor;
                const endN = cursor + k - 1;
                cursor = endN + 1;

                let label = "-";
                if (totalFees) {
                    label = (k > 1)
                        ? `${Array.from({ length: k }, (_, j) => startN + j).join("-")}/${totalFees}`
                        : `${startN}/${totalFees}`;
                }

                if (payId) map.set(payId, label);
            });
        });

        return map;
    }, [listMovilesPay, listDueCredit, TZ]);


    const fmScheduleMetaMap = useMemo(() => {
        const map = new Map();
        (listDueFm || []).forEach((r) => {
            const key = String(r?.fm_id ?? "");
            if (!key) return;
            map.set(key, getScheduleMeta(r?.fm_payment_schedule));
        });
        return map;
        // eslint-disable-next-line
    }, [listDueFm, selectedDDMMYYYY]);

    const fmDueFeeLabelByPaymentId = useMemo(() => {
        const map = new Map(); // fmp_id -> "4/10" o "4-5-6/10"

        const toNum = (v) => {
            const n = Number(String(v ?? "").replace(/S\//gi, "").replace(/,/g, "").trim());
            return Number.isFinite(n) ? n : 0;
        };

        // agrupar solo filas con pago del día (fmp_id)
        const groups = new Map();
        (listDueFm || []).forEach((r) => {
            const fmId = String(r?.fm_id ?? "");
            const payId = String(r?.fmp_id ?? "");
            if (!fmId || !payId) return;
            const arr = groups.get(fmId) || [];
            arr.push(r);
            groups.set(fmId, arr);
        });

        groups.forEach((rows, fmId) => {
            // ordenar asc por fecha/hora de pago
            rows.sort((a, b) => {
                const ma = moment.tz(String(a?.fmp_current_date || ""), "DD-MM-YYYY HH:mm:ss", true, TZ);
                const mb = moment.tz(String(b?.fmp_current_date || ""), "DD-MM-YYYY HH:mm:ss", true, TZ);
                return ma.valueOf() - mb.valueOf();
            });

            // usa meta ya calculado desde el cronograma (es lo más confiable)
            const meta = fmScheduleMetaMap.get(String(fmId));
            const totalFees = Number(meta?.total ?? 0);
            const paidTotal = Number(meta?.paid ?? 0);

            const feePerQuota = toNum(rows?.[0]?.fee);

            // cuántas cuotas representa cada registro (si pagó 3 juntas, fmp_fee ≈ 3*fee)
            const counts = rows.map((r) => {
                const amt = toNum(r?.fmp_fee);
                if (!amt || !feePerQuota) return 1;
                const k = Math.max(1, Math.round(amt / feePerQuota));
                const diff = Math.abs(amt - k * feePerQuota);
                const tol = 0.25 * k; // tolerancia
                return diff <= tol ? k : 1;
            });

            const paidTodayTotal = counts.reduce((s, x) => s + (Number(x) || 0), 0);

            // primera cuota pagada hoy = pagadasTotales - pagadasHoy + 1
            let cursor = Math.max(1, (paidTotal - paidTodayTotal + 1));

            rows.forEach((r, i) => {
                const payId = String(r?.fmp_id ?? "");
                const k = Math.max(1, Number(counts[i] || 1));

                const startN = cursor;
                const endN = cursor + k - 1;
                cursor = endN + 1;

                let label = "-";
                if (totalFees) {
                    label = (k > 1)
                        ? `${Array.from({ length: k }, (_, j) => startN + j).join("-")}/${totalFees}`
                        : `${startN}/${totalFees}`;
                }

                if (payId) map.set(payId, label);
            });
        });

        return map;
    }, [listDueFm, fmScheduleMetaMap, TZ]);


    /* =========================== DUPLICADOS POR DNI (SOLO VISUAL) =========================== */
    const normalizeDni = (dni) => String(dni ?? "").trim();


    const dueCreditStats = useMemo(() => {
        const rows = listDueCredit || [];

        let totalFee = 0;
        let paidAmount = 0;
        let paidCount = 0;
        let confirmedCount = 0;


        const paidUsersSet = new Set();
        rows.forEach((r) => {
            totalFee += parseMoney(r?.fee);
            const hasPayment = Boolean(r?.payment_id);
            if (hasPayment) {
                paidCount += 1;
                paidAmount += parseMoney(r?.fr_fee);
                const __dni = normalizeDni(r?.dni);
                if (__dni) paidUsersSet.add(__dni);
                if (String(r?.fp_check) === "1") confirmedCount += 1;
            }
        });

        const totalCount = rows.length;
        const pendingCount = Math.max(0, totalCount - paidCount);
        const toConfirmCount = Math.max(0, paidCount - confirmedCount);
        const pct = totalCount ? Math.round((paidCount / totalCount) * 100) : 0;

        return {
            totalCount,
            paidCount,

            paidUsersCount: paidUsersSet.size,
            pendingCount,
            confirmedCount,
            toConfirmCount,
            totalFee,
            paidAmount,
            pendingAmount: Math.max(0, totalFee - paidAmount),
            pct,
        };
    }, [listDueCredit]);

    const dueFmStats = useMemo(() => {
        const rows = listDueFm || [];

        let totalFee = 0;
        let paidAmount = 0;
        let confirmedCount = 0;

        const dueKeys = new Set();
        const schedulePaidKeys = new Set();
        const paymentIds = new Set();
        const confirmedPaymentIds = new Set();
        const paidUsersSet = new Set();

        rows.forEach((r) => {
            const fmId = String(r?.fm_id ?? "").trim();
            if (!fmId) return;

            const dueDate = String(r?.due_date || selectedDDMMYYYY || "").trim();
            const dueKey = `${fmId}__${dueDate}`;

            if (!dueKeys.has(dueKey)) {
                dueKeys.add(dueKey);
                totalFee += parseMoney(r?.fee);

                const meta = fmScheduleMetaMap.get(fmId);
                const todayItem = meta?.todayItem;
                const dueState = String(r?.due_state ?? todayItem?.state ?? "");
                if (dueState === "1") {
                    schedulePaidKeys.add(dueKey);
                    const __dni = normalizeDni(r?.dni);
                    if (__dni) paidUsersSet.add(__dni);
                }
            }

            const payId = String(r?.fmp_id ?? "").trim();
            if (payId && !paymentIds.has(payId)) {
                paymentIds.add(payId);
                paidAmount += parseMoney(r?.fmp_fee);

                const __dni = normalizeDni(r?.dni);
                if (__dni) paidUsersSet.add(__dni);

                if (String(r?.fmp_check) === "1") {
                    confirmedPaymentIds.add(payId);
                    confirmedCount += 1;
                }
            }
        });

        const totalCount = dueKeys.size;
        const schedulePaidToday = schedulePaidKeys.size;
        const schedulePendingToday = Math.max(0, totalCount - schedulePaidToday);
        const paidCount = paymentIds.size;
        const pendingCount = Math.max(0, totalCount - paidCount);
        const toConfirmCount = Math.max(0, paidCount - confirmedPaymentIds.size);
        const pct = totalCount ? Math.round((schedulePaidToday / totalCount) * 100) : 0;

        return {
            totalCount,
            paidCount,
            paidUsersCount: paidUsersSet.size,
            pendingCount,
            confirmedCount,
            toConfirmCount,
            totalFee,
            paidAmount,
            pendingAmount: Math.max(0, totalFee - paidAmount),
            pct,
            schedulePaidToday,
            schedulePendingToday,
        };
        // eslint-disable-next-line
    }, [listDueFm, fmScheduleMetaMap, selectedDDMMYYYY]);

    const renderMoneySummary = (totals) => {
        const ef = moneyFmt(totals?.efectivo || 0);
        const on = moneyFmt(totals?.online || 0);
        const tt = moneyFmt(totals?.total || 0);

        return (
            <div className="installmentMoneySummary" role="group" aria-label="Resumen de montos del día">
                {/* <div className="moneyPill moneyPillCash" title="Monto en efectivo">
                    <div className="moneyPillLabel">Efectivo</div>
                    <div className="moneyPillValue">S/ {ef}</div>
                </div>

                <div className="moneyPill moneyPillOnline" title="Monto online (Yape/Plin/Transferencia/Tarjeta)">
                    <div className="moneyPillLabel">Online</div>
                    <div className="moneyPillValue">S/ {on}</div>
                </div> */}

                <div className="moneyPill moneyPillTotal" title="Monto total del día">
                    <div className="moneyPillLabel">Total</div>
                    <div className="moneyPillValue">S/ {tt}</div>
                </div>
            </div>
        );
    };

    const renderTotalPill = (label, totalValue, title) => {
        return (
            <div className="installmentMoneySummary" role="group" aria-label={label}>
                <div className="moneyPill moneyPillTotal" title={title || label}>
                    <div className="moneyPillLabel">{label}</div>
                    <div className="moneyPillValue">S/ {moneyFmt(totalValue || 0)}</div>
                </div>
            </div>
        );
    };

    const renderCountPill = (label, countValue, title) => {
        return (
            <div className="installmentMoneySummary" role="group" aria-label={label}>
                <div className="moneyPill moneyPillTotal" title={title || label}>
                    <div className="moneyPillLabel">{label}</div>
                    <div className="moneyPillValue">{Number(countValue || 0)}</div>
                </div>
            </div>
        );
    };


    const renderCheckText = (checkValue, hasPayment) => {
        const checked = String(checkValue) === "1";
        if (checked) {
            return <span className="checkText checked">Confirmado</span>;
        }
        return (
            <span className="checkText pending">{hasPayment ? "Por confirmar" : "Sin pago"}</span>
        );
    };

    const renderKpiPill = (label, value, tone = "neutral") => {
        return (
            <div className={`kpiPill kpiPill-${tone}`} title={label}>
                <div className="kpiPillLabel">{label}</div>
                <div className="kpiPillValue">{value}</div>
            </div>
        );
    };

    const renderKpiSection = () => {
        const overallTotal = (dueCreditStats?.totalCount || 0) + (dueFmStats?.totalCount || 0);
        const overallPaid = (dueCreditStats?.paidCount || 0) + (dueFmStats?.schedulePaidToday || 0);
        const overallPending = Math.max(0, overallTotal - overallPaid);
        const overallPct = overallTotal ? Math.round((overallPaid / overallTotal) * 100) : 0;

        const overallTotalFee = parseMoney(dueCreditStats?.totalFee) + parseMoney(dueFmStats?.totalFee);
        const overallPaidAmount = parseMoney(dueCreditStats?.paidAmount) + parseMoney(dueFmStats?.paidAmount);
        const overallPendingAmount = Math.max(0, overallTotalFee - overallPaidAmount);

        return (
            <div className="installmentKpiGrid" role="region" aria-label="KPIs de cuotas que vencen hoy">
                <div className="installmentKpiBlock">
                    <div className="installmentKpiBlockTitle">Vencen hoy — Créditos</div>
                    <div className="installmentKpiPills">
                        {renderKpiPill("Cuotas (total)", dueCreditStats?.totalCount || 0)}
                        {renderKpiPill("Cuotas pagadas", dueCreditStats?.paidCount || 0, "ok")}
                        {renderKpiPill("Cuotas pendientes", dueCreditStats?.pendingCount || 0, "danger")}
                        {renderKpiPill("Confirmadas", dueCreditStats?.confirmedCount || 0, "ok")}
                        {renderKpiPill("Por confirmar", dueCreditStats?.toConfirmCount || 0, "warn")}
                        {renderKpiPill("% cumplimiento", `${dueCreditStats?.pct || 0}%`)}
                        {renderKpiPill("Monto del día", `S/ ${moneyFmt(dueCreditStats?.totalFee || 0)}`)}
                        {renderKpiPill("Monto cobrado", `S/ ${moneyFmt(dueCreditStats?.paidAmount || 0)}`, "ok")}
                        {renderKpiPill(
                            "Monto pendiente",
                            `S/ ${moneyFmt(dueCreditStats?.pendingAmount || 0)}`,
                            "danger"
                        )}
                    </div>
                </div>

                <div className="installmentKpiBlock">
                    <div className="installmentKpiBlockTitle">Vencen hoy — FM</div>
                    <div className="installmentKpiPills">
                        {renderKpiPill("Cuotas (total)", dueFmStats?.totalCount || 0)}
                        {renderKpiPill("Cuotas pagadas", dueFmStats?.schedulePaidToday || 0, "ok")}
                        {renderKpiPill("Cuotas pendientes", dueFmStats?.schedulePendingToday || 0, "danger")}
                        {renderKpiPill("Pagos registrados", dueFmStats?.paidCount || 0)}
                        {renderKpiPill("Confirmadas", dueFmStats?.confirmedCount || 0, "ok")}
                        {renderKpiPill("Por confirmar", dueFmStats?.toConfirmCount || 0, "warn")}
                        {renderKpiPill("% cumplimiento", `${dueFmStats?.pct || 0}%`)}
                        {renderKpiPill("Monto del día", `S/ ${moneyFmt(dueFmStats?.totalFee || 0)}`)}
                        {renderKpiPill("Monto cobrado", `S/ ${moneyFmt(dueFmStats?.paidAmount || 0)}`, "ok")}
                        {renderKpiPill(
                            "Monto pendiente",
                            `S/ ${moneyFmt(dueFmStats?.pendingAmount || 0)}`,
                            "danger"
                        )}
                    </div>
                </div>

                <div className="installmentKpiBlock installmentKpiBlockOverall">
                    <div className="installmentKpiBlockTitle">Resumen general — Vencen hoy</div>
                    <div className="installmentKpiPills">
                        {renderKpiPill("Cuotas (total)", overallTotal)}
                        {renderKpiPill("Cuotas pagadas", overallPaid, "ok")}
                        {renderKpiPill("Cuotas pendientes", overallPending, "danger")}
                        {renderKpiPill("% cumplimiento", `${overallPct}%`)}
                        {renderKpiPill("Monto total", `S/ ${moneyFmt(overallTotalFee)}`)}
                        {renderKpiPill("Monto cobrado", `S/ ${moneyFmt(overallPaidAmount)}`, "ok")}
                        {renderKpiPill("Monto pendiente", `S/ ${moneyFmt(overallPendingAmount)}`, "danger")}
                    </div>
                </div>
            </div>
        );
    };

    // Conteo de repetidos por clave (DNI u otra)
    const buildDupMapByKey = (rows = [], getKey) => {
        const countMap = new Map();
        (rows || []).forEach((r) => {
            const key = String(getKey?.(r) ?? "").trim();
            if (!key) return;
            countMap.set(key, (countMap.get(key) || 0) + 1);
        });
        return countMap;
    };

    // Construye un "groupMap" SOLO para claves duplicadas, para asignar un color/grupo por cada DNI duplicado.
    // groupIndex empieza en 1 y se incrementa por cada DNI duplicado distinto.
    const buildDupGroupMapByKey = (rows = [], getKey) => {
        // IMPORTANTE: countMap se mantiene (tal como pediste) y se usa para detectar duplicados.
        const countMap = new Map();
        (rows || []).forEach((r) => {
            const key = String(getKey?.(r) ?? "").trim();
            if (!key) return;
            countMap.set(key, (countMap.get(key) || 0) + 1);
        });

        const groupMap = new Map();
        let groupIndex = 1;

        // Solo asigna grupo a los que realmente son duplicados (>1)
        countMap.forEach((count, key) => {
            if (count > 1) {
                groupMap.set(key, groupIndex);
                groupIndex += 1;
            }
        });

        return groupMap;
    };

    const isDupByKey = (key, map) => (map.get(key) || 0) > 1;

    const getDupGroupClass = (key, dupCountMap, dupGroupMap) => {
        const k = String(key ?? "").trim();
        if (!k) return "";
        const duplicated = isDupByKey(k, dupCountMap);
        if (!duplicated) return "";

        const groupIndex = dupGroupMap.get(k) || 0; // 1..n
        if (!groupIndex) return "";

        // 0..DUP_GROUPS-1 (recicla estilos si hay más grupos)
        const paletteIndex = (groupIndex - 1) % DUP_GROUPS;
        return `dupGroup-${paletteIndex}`;
    };

    const dupMapDniMovilesPay = useMemo(
        () => buildDupMapByKey(listMovilesPay, (r) => normalizeDni(r?.dni)),
        [listMovilesPay]
    );

    const dupMapDniFM = useMemo(
        () => buildDupMapByKey(listFmMovilesPay, (r) => normalizeDni(r?.dni)),
        [listFmMovilesPay]
    );

    // Nuevo: mapa de grupos por DNI (solo para DNIs duplicados) para diferenciar colores entre distintos DNIs duplicados.
    const dupGroupMapDniMovilesPay = useMemo(
        () => buildDupGroupMapByKey(listMovilesPay, (r) => normalizeDni(r?.dni)),
        [listMovilesPay]
    );

    const dupGroupMapDniFM = useMemo(
        () => buildDupGroupMapByKey(listFmMovilesPay, (r) => normalizeDni(r?.dni)),
        [listFmMovilesPay]
    );

    const dupMapDniDueCredit = useMemo(
        () => buildDupMapByKey(listDueCredit, (r) => normalizeDni(r?.dni)),
        [listDueCredit]
    );

    const dupMapDniDueFm = useMemo(
        () => buildDupMapByKey(listDueFm, (r) => normalizeDni(r?.dni)),
        [listDueFm]
    );

    const dupGroupMapDniDueCredit = useMemo(
        () => buildDupGroupMapByKey(listDueCredit, (r) => normalizeDni(r?.dni)),
        [listDueCredit]
    );

    const dupGroupMapDniDueFm = useMemo(
        () => buildDupGroupMapByKey(listDueFm, (r) => normalizeDni(r?.dni)),
        [listDueFm]
    );

    // Solo para indicadores visuales (punto) en el header del card.
    const hasDupMP = useMemo(
        () => [...dupMapDniMovilesPay.values()].some((c) => c > 1),
        [dupMapDniMovilesPay]
    );
    const hasDupFM = useMemo(
        () => [...dupMapDniFM.values()].some((c) => c > 1),
        [dupMapDniFM]
    );

    const hasDupDueCredit = useMemo(
        () => [...dupMapDniDueCredit.values()].some((c) => c > 1),
        [dupMapDniDueCredit]
    );
    const hasDupDueFm = useMemo(
        () => [...dupMapDniDueFm.values()].some((c) => c > 1),
        [dupMapDniDueFm]
    );

    const renderDniCell = (dni, dupCountMap, dupGroupMap) => {
        const v = normalizeDni(dni);
        const duplicated = v && isDupByKey(v, dupCountMap);
        const groupClass = getDupGroupClass(v, dupCountMap, dupGroupMap);

        // CERO texto adicional. Solo estilo por clase.
        return (
            <div className={`dniCell ${duplicated ? "dniCellDup" : ""} ${groupClass}`}>
                <span className="dniValue">{v || "-"}</span>
                <span className={`dupDot ${duplicated ? "on" : ""}`} aria-hidden="true" />
            </div>
        );
    };

    const confirmPayment = async (paymentId, isFm = false) => {
        const ok = window.confirm("¿Confirmar la verificación de este pago de cuota?");
        if (!ok) return;

        try {
            const token = localStorage.getItem("token");
            const url = isFm
                ? `${process.env.REACT_APP_API}/installments/confirm-fm`
                : `${process.env.REACT_APP_API}/installments/confirm`;

            await axios.post(
                url,
                { paymentId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            fetchInstallments(selectedRange);
        } catch (error) {
            alert("Error al confirmar el pago");
        }
    };

    const renderTableMovilesPay = () => (
        <div className="parentTable tableScroll">
            <table className="table tableSticky">
                <thead>
                    <tr>
                        <th>N°</th>
                        <th>Gestor</th>
                        <th>Asesor</th>
                        <th>Cliente</th>
                        <th>DNI</th>
                        <th>Celular</th>
                        <th>Equipo</th>
                        <th width={"5%"}>Cuota</th>
                        <th>N° Cuota</th>
                        <th>Vencidas</th>
                        <th>Monto Pag.</th>
                        <th>Fecha Pago</th>
                        <th>Venc.</th>
                        <th>Confirmar</th>
                    </tr>
                </thead>
                <tbody>
                    {(listMovilesPay || []).map((p, idx, arr) => {
                        const seller = `${p?.seller_user || ""} ${p?.seller_last || ""}`.trim();
                        const fullName = `${p?.name || ""} ${p?.last_name || ""}`.trim();

                        const dniKey = normalizeDni(p?.dni);
                        const duplicated = dniKey && isDupByKey(dniKey, dupMapDniMovilesPay);
                        const groupClass = getDupGroupClass(
                            dniKey,
                            dupMapDniMovilesPay,
                            dupGroupMapDniMovilesPay
                        );

                        return (
                            <tr
                                key={idx}
                                onClick={() => navigate(`/credit/${p?.credit_id}`)}
                                style={{ cursor: "pointer" }}
                                title="Ver crédito"
                                className={`rowStatus ${String(p?.fp_check) === "1" ? "confirmed" : "pending"
                                    } ${duplicated ? "dupRow" : ""} ${groupClass}`}
                            >
                                {/* <td>{idx + 1}</td> */}
                                <td>{arr.length - idx}</td>
                                <td>{formatCobranzaShort(p?.cobranza_user, p?.cobranza_last)}</td>
                                <td>{seller || "-"}</td>
                                <td>{fullName || "-"}</td>
                                <td>
                                    {renderDniCell(
                                        p?.dni,
                                        dupMapDniMovilesPay,
                                        dupGroupMapDniMovilesPay
                                    )}
                                </td>
                                <td>{p?.phone_number || "-"}</td>
                                <td>{p?.phone_name || "-"}</td>
                                <td width={"5%"}><strong>S/ {moneyFmt(p?.fee)}</strong></td>
                                <td>{creditFeeLabelByPaymentId.get(String(p?.fr_id ?? "")) || p?.fr_fee_ratio || "-"}</td>
                                <td>{countOverdueFromSchedule(p?.credit_schedule)}</td>
                                <td>S/ {p?.fr_fee || "-"}</td>
                                <td>{p?.fr_current_date || ""}</td>
                                <td>{p?.fr_expire_paid || p?.fr_expire || "-"}</td>
                                <td>
                                    <button
                                        className={`btnCheckIns ${p.fp_check === "1" ? "checked" : "pending"
                                            }`}
                                        // UX: si está duplicado, no permitas confirmar (evita error humano)
                                        // disabled={p.fp_check === "1" || duplicated}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (p.fp_check === "1") return;
                                            confirmPayment(p.fr_id);
                                        }}
                                    >
                                        {p.fp_check === "1" ? "Confirmado" : "Confirmar"}
                                    </button>
                                </td>
                            </tr>
                        );
                    })}

                    {(!listMovilesPay || listMovilesPay.length === 0) && (
                        <tr>
                            <td colSpan="15" className="installmentEmptyRow">
                                Sin pagos registrados para este día.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    const renderTableFmMovilesPay = () => (
        <div className="parentTable tableScroll">
            <table className="table tableSticky">
                <thead>
                    <tr>
                        <th>N°</th>
                        <th>Fuente</th>
                        <th>Gestor</th>
                        <th>Asesor</th>
                        <th>Cliente</th>
                        <th>DNI</th>
                        <th>Celular</th>
                        <th>Equipo</th>
                        <th width={"5%"}>Cuota</th>
                        <th>N° Cuota</th>
                        <th>Vencidas</th>
                        <th>Monto Pag.</th>
                        <th>Fecha Pago</th>
                        <th>Venc.</th>
                        <th>Confirmar</th>
                    </tr>
                </thead>
                <tbody>
                    {(listFmMovilesPay || []).map((p, idx, arr) => {
                        const seller = `${p?.seller_user || ""} ${p?.seller_last || ""}`.trim();
                        const fullName = `${p?.name || ""} ${p?.last_name || ""}`.trim();

                        const dniKey = normalizeDni(p?.dni);
                        const duplicated = dniKey && isDupByKey(dniKey, dupMapDniFM);
                        const groupClass = getDupGroupClass(dniKey, dupMapDniFM, dupGroupMapDniFM);

                        return (
                            <tr
                                key={idx}
                                onClick={() => navigate(`/fm/${p?.fm_id}`)}
                                style={{ cursor: "pointer" }}
                                title="Ver FM"
                                className={`rowStatus ${String(p?.fmp_check) === "1" ? "confirmed" : "pending"
                                    } ${duplicated ? "dupRow" : ""} ${groupClass}`}
                            >
                                <td>{arr.length - idx}</td>
                                <td>{renderFmSourceBadge(p?.fm_source)}</td>
                                <td>{formatCobranzaShort(p?.cobranza_user, p?.cobranza_last)}</td>
                                <td>{seller || "-"}</td>
                                <td>{fullName || "-"}</td>
                                <td>{renderDniCell(p?.dni, dupMapDniFM, dupGroupMapDniFM)}</td>
                                <td>{p?.phone_number || "-"}</td>
                                <td>{p?.phone_name || "-"}</td>
                                <td width={"5%"}><strong>S/ {moneyFmt(p?.fee)}</strong></td>
                                {/* <td>{p?.fm_fee_ratio || "-"}</td> */}
                                <td>{p?.fm_fee_label || p?.fm_fee_ratio || "-"}</td>
                                <td>{countOverdueFromSchedule(p?.fm_payment_schedule)}</td>
                                <td width={"5%"}>S/ {p?.fmp_fee || "-"}</td>
                                <td>{p?.fmp_current_date || ""}</td>
                                <td>{p?.fmp_expire_paid || p?.fmp_expire || "-"}</td>
                                <td>
                                    <button
                                        className={`btnCheckIns ${p.fmp_check === "1" ? "checked" : "pending"
                                            }`}
                                        // Si deseas también bloquear por duplicado en FM, descomenta:
                                        // disabled={p.fmp_check === "1" || duplicated}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (p.fmp_check === "1") return;
                                            confirmPayment(p.fmp_id, true);
                                        }}
                                    >
                                        {p.fmp_check === "1" ? "Confirmado" : "Confirmar"}
                                    </button>
                                </td>
                            </tr>
                        );
                    })}

                    {(!listFmMovilesPay || listFmMovilesPay.length === 0) && (
                        <tr>
                            <td colSpan="15" className="installmentEmptyRow">
                                Sin pagos FM registrados para este día.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    const renderTableDueCredit = () => (
        <div className="parentTable tableScroll">
            <table className="table tableSticky">
                <thead>
                    <tr>
                        <th>N°</th>
                        <th>Gestor</th>
                        <th>Asesor</th>
                        <th>Cliente</th>
                        <th>DNI</th>
                        <th>Celular</th>
                        <th>Equipo</th>
                        <th width={"5%"}>Cuota</th>
                        <th>N° Cuota</th>
                        <th>Vencidas</th>
                        <th>Deuda</th>
                        <th>Monto Pag.</th>
                        <th>Fecha Pago</th>
                        <th>Venc.</th>
                        <th>Confirmar</th>
                    </tr>
                </thead>
                <tbody>
                    {(listDueCredit || []).map((p, idx, arr) => {
                        const seller = `${p?.seller_user || ""} ${p?.seller_last || ""}`.trim();
                        const fullName = `${p?.name || ""} ${p?.last_name || ""}`.trim();

                        const dniKey = normalizeDni(p?.dni);
                        const duplicated = dniKey && isDupByKey(dniKey, dupMapDniDueCredit);
                        const groupClass = getDupGroupClass(
                            dniKey,
                            dupMapDniDueCredit,
                            dupGroupMapDniDueCredit
                        );

                        const hasPayment = Number(p?.payment_id || 0) > 0;
                        const isChecked = String(p?.fp_check) === "1";

                        const payId = String(p?.payment_id ?? "");
                        const cuotaLabel = hasPayment
                            ? (creditFeeLabelByPaymentId.get(payId) || p?.due_fee_ratio || "-")
                            : (p?.due_fee_ratio || "-");


                        return (
                            <tr
                                key={idx}
                                onClick={() => navigate(`/credit/${p?.credit_id}`)}
                                style={{ cursor: "pointer" }}
                                title="Ver crédito"
                                className={`rowStatus ${isChecked ? "confirmed" : "pending"} ${duplicated ? "dupRow" : ""} ${groupClass}`}
                            >
                                <td>{arr.length - idx}</td>
                                <td>{formatCobranzaShort(p?.cobranza_user, p?.cobranza_last)}</td>
                                <td>{seller || "-"}</td>
                                <td>{fullName || "-"}</td>
                                <td>{renderDniCell(p?.dni, dupMapDniDueCredit, dupGroupMapDniDueCredit)}</td>
                                <td>{p?.phone_number || "-"}</td>
                                <td>{p?.phone_name || "-"}</td>
                                <td width={"5%"}><strong>S/ {moneyFmt(p?.fee) || "-"}</strong></td>
                                <td>{cuotaLabel}</td>
                                <td>{countOverdueFromSchedule(p?.fr_days_payment)}</td>
                                <td>{(() => { const v = countOverdueFromSchedule(p?.fr_days_payment); const cuota = parseMoney(p?.fee); const d = hasPayment ? (cuota * Number(v || 0)) : (cuota * (Number(v || 0) + 1)); return `S/ ${moneyFmt(d)}`; })()}</td>
                                <td width={"5%"}>{hasPayment ? `S/ ${p?.fr_fee || "-"}` : "-"}</td>
                                <td>{hasPayment ? (p?.fr_current_date || "") : "-"}</td>
                                {/* <td>{selectedDDMMYYYY}</td> */}
                                <td>{p?.venc_last_paid || "-"}</td>
                                <td>{renderCheckText(p?.fp_check, hasPayment)}</td>
                            </tr>
                        );
                    })}

                    {(!listDueCredit || listDueCredit.length === 0) && (
                        <tr>
                            <td colSpan="15" className="installmentEmptyRow">
                                Sin cuotas de créditos que venzan en esta fecha.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    const renderTableDueFm = () => (
        <div className="parentTable tableScroll">
            <table className="table tableSticky">
                <thead>
                    <tr>
                        <th>N°</th>
                        <th>Fuente</th>
                        <th>Gestor</th>
                        <th>Asesor</th>
                        <th>Cliente</th>
                        <th>DNI</th>
                        <th>Celular</th>
                        <th>Equipo</th>
                        <th width={"5%"}>Cuota</th>
                        <th>N° Cuota</th>
                        <th>Vencidas</th>
                        <th>Deuda</th>
                        <th>Monto Pag.</th>
                        <th>Fecha Pago</th>
                        <th>Venc.</th>
                        <th>Confirmar</th>
                    </tr>
                </thead>
                <tbody>
                    {(listDueFm || []).map((p, idx, arr) => {
                        const seller = `${p?.seller_user || ""} ${p?.seller_last || ""}`.trim();
                        const fullName = `${p?.name || ""} ${p?.last_name || ""}`.trim();

                        const dniKey = normalizeDni(p?.dni);
                        const duplicated = dniKey && isDupByKey(dniKey, dupMapDniDueFm);
                        const groupClass = getDupGroupClass(dniKey, dupMapDniDueFm, dupGroupMapDniDueFm);

                        // const hasPayment = Number(p?.fmp_id || 0) > 0;
                        const isChecked = String(p?.fmp_check) === "1";

                        const meta = fmScheduleMetaMap.get(String(p?.fm_id ?? ""));
                        const total = Number(meta?.total ?? 0);
                        const paid = Number(meta?.paid ?? 0);

                        const hasPayment = Number(p?.fmp_id || 0) > 0;
                        const payIdKey = String(p?.fmp_id ?? "");
                        const labelFromPay = payIdKey ? fmDueFeeLabelByPaymentId.get(payIdKey) : null;

                        // ✅ regla final:
                        // - Si hay pago hoy -> usa label por registro (4/10 o 4-5-6/10)
                        // - Si no hay pago -> muestra pagadas/total (0/5, 2/6, etc)
                        const cuotaLabel = hasPayment
                            ? (labelFromPay || (total ? `${paid}/${total}` : "-"))
                            : (total ? `${paid}/${total}` : "-");


                        return (
                            <tr
                                key={idx}
                                onClick={() => navigate(`/fm/${p?.fm_id}`)}
                                style={{ cursor: "pointer" }}
                                title="Ver FM"
                                className={`rowStatus ${isChecked ? "confirmed" : "pending"} ${duplicated ? "dupRow" : ""} ${groupClass}`}
                            >
                                <td>{arr.length - idx}</td>
                                <td>{renderFmSourceBadge(p?.fm_source)}</td>
                                <td>{formatCobranzaShort(p?.cobranza_user, p?.cobranza_last)}</td>
                                <td>{seller || "-"}</td>
                                <td>{fullName || "-"}</td>
                                <td>{renderDniCell(p?.dni, dupMapDniDueFm, dupGroupMapDniDueFm)}</td>
                                <td>{p?.phone_number || "-"}</td>
                                <td>{p?.phone_name || "-"}</td>
                                <td width={"5%"}><strong>S/ {moneyFmt(p?.fee) || "-"}</strong></td>
                                <td>{cuotaLabel}</td>
                                <td>{countOverdueFromSchedule(p?.fm_payment_schedule)}</td>
                                <td>{(() => { const v = countOverdueFromSchedule(p?.fm_payment_schedule); const cuota = parseMoney(p?.fee); const d = hasPayment ? (cuota * Number(v || 0)) : (cuota * (Number(v || 0) + 1)); return `S/ ${moneyFmt(d)}`; })()}</td>
                                <td width={"5%"}>{hasPayment ? `S/ ${p?.fmp_fee || "-"}` : "-"}</td>
                                <td>{hasPayment ? (p?.fmp_current_date || "") : "-"}</td>
                                <td>{selectedDDMMYYYY}</td>
                                <td>{renderCheckText(p?.fmp_check, hasPayment)}</td>
                            </tr>
                        );
                    })}

                    {(!listDueFm || listDueFm.length === 0) && (
                        <tr>
                            <td colSpan="16" className="installmentEmptyRow">
                                Sin cuotas FM que venzan en esta fecha.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    return (
        <div>
            {loading && <PhoneLoader />}
            <ScrollToTop />
            <Header user={user} namePage={props.namePage} />

            <section className="sectionBody sectionBodyCredits sectionBodyInstallment">
                <div className="containerCustom containerBody">
                    <div className="blockBody installmentBlock">
                        <div className="installmentTopHeader">
                            <div>
                                <h3 className="installmentTitle">Pagos de Cuotas (MovilesPay)</h3>
                                <div className="installmentSubTitle">
                                    Consulta por fecha los pagos de cuotas MovilesPay y cuotas FM MovilesPay.
                                </div>
                            </div>

                            <div className="calendarFilterWrapper installmentCalendar">
                                {showText && <div className="calendarFilterMsg">{showText}</div>}

                                <label htmlFor="calendarFilter" className="installmentCalendarIcon">
                                    <ImCalendar />
                                </label>

                                <input
                                    readOnly
                                    ref={calendarInputRef}
                                    className="filterDate installmentCalendarInput"
                                    placeholder="Selecciona rango de fechas"
                                    id="calendarFilter"
                                />
                            </div>
                        </div>

                        {renderKpiSection()}

                        <div className="installmentColumns">
                            <div className="installmentCard installmentCardOne">
                                <div className="installmentCardHeader">
                                    <div className="installmentHeaderTitleWrap">
                                        <div className="installmentCardTitle">Cuotas MovilesPay</div>
                                        <span className={`headerDot ${hasDupMP ? "on" : ""}`} aria-hidden="true" />
                                        <div className="installmentCardSubtitle">
                                            Pagos registrados en la fecha seleccionada
                                        </div>
                                    </div>

                                    {/* NUEVO: resumen + badge (resumen a la izquierda del badge) */}
                                    <div className="installmentHeaderRight">
                                        {renderMoneySummary(totalsMovilesPay)}
                                        <div className="installmentBadge">{listMovilesPay?.length || 0}</div>
                                    </div>
                                </div>

                                <div className="installmentCardBody">{renderTableMovilesPay()}</div>
                            </div>

                            <div className="installmentCard installmentCardThree">
                                <div className="installmentCardHeader">
                                    <div className="installmentHeaderTitleWrap">
                                        <div className="installmentCardTitle">Cuotas que vencen hoy (Créditos)</div>
                                        <span
                                            className={`headerDot ${hasDupDueCredit ? "on" : ""}`}
                                            aria-hidden="true"
                                        />
                                        <div className="installmentCardSubtitle">
                                            Créditos con cuota programada para {selectedDDMMYYYY}
                                        </div>
                                    </div>

                                    <div className="installmentHeaderRight">
                                        {renderCountPill("Cuotas cobradas", dueCreditStats?.paidUsersCount || 0)}
                                        {renderTotalPill("Monto cobrado", dueCreditStats?.paidAmount || 0)}
                                        <div className="installmentBadge">{listDueCredit?.length || 0}</div>
                                    </div>
                                </div>

                                <div className="installmentCardBody">{renderTableDueCredit()}</div>
                            </div>

                            <div className="installmentCard installmentCardTwo">
                                <div className="installmentCardHeader">
                                    <div className="installmentHeaderTitleWrap">
                                        <div className="installmentCardTitle">Cuotas FM</div>
                                        <span className={`headerDot ${hasDupFM ? "on" : ""}`} aria-hidden="true" />
                                        <div className="installmentCardSubtitle">
                                            Pagos FM registrados en la fecha seleccionada
                                        </div>
                                    </div>

                                    {/* NUEVO: resumen + badge (resumen a la izquierda del badge) */}
                                    <div className="installmentHeaderRight">
                                        {renderMoneySummary(totalsFm)}
                                        <div className="installmentBadge">{listFmMovilesPay?.length || 0}</div>
                                    </div>
                                </div>

                                <div className="installmentCardBody">{renderTableFmMovilesPay()}</div>
                            </div>

                            <div className="installmentCard installmentCardFour">
                                <div className="installmentCardHeader">
                                    <div className="installmentHeaderTitleWrap">
                                        <div className="installmentCardTitle">Cuotas que vencen hoy (FM)</div>
                                        <span
                                            className={`headerDot ${hasDupDueFm ? "on" : ""}`}
                                            aria-hidden="true"
                                        />
                                        <div className="installmentCardSubtitle">
                                            FM con cuota programada para {selectedDDMMYYYY}
                                        </div>
                                    </div>

                                    <div className="installmentHeaderRight">
                                        {renderCountPill("Cuotas cobradas", dueFmStats?.paidUsersCount || 0)}
                                        {renderTotalPill("Monto cobrado", dueFmStats?.paidAmount || 0)}
                                        <div className="installmentBadge">{listDueFm?.length || 0}</div>
                                    </div>
                                </div>

                                <div className="installmentCardBody">{renderTableDueFm()}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

export default Installment;
